import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  Users, IndianRupeeIcon, CheckCircle, Filter, RefreshCw, AlertCircle, TrendingUp, Award, Target, CheckSquare
} from 'lucide-react';
import { apiService } from '../../services/api.service';

interface DashboardData {
  overview: {
    total_beneficiaries: number;
    successful_beneficiaries: number;
    success_rate_percentage?: number;
    total_disbursed_amount: {
      crores: number;
    };
    penny_drop_amount: {
      crores: number;
    };
    total_paid_amount: {
      crores: number;
    };
    total_successful_transactions: number;

    // ✅ Three penny drop metrics
    unique_penny_drops_done: number;
    total_penny_drop_transactions: number;
    penny_drop_success_transactions: number;
    total_beneficiaries_with_penny_drop_attempt: number;
    penny_drop_success_rate: number;

    unique_banks_involved: number;
    districts_covered: number;
  };
  stage_wise: Array<{
    stage_code: string;
    stage_name: string;
    beneficiary_count: number;
    total_amount: {
      crores: number;
    };
  }>;
  payment_methods: Array<{
    payment_method: string;
    beneficiary_count: number;
    percentage: number;
    total_amount: {
      crores: number;
    };
  }>;
  weekly_trend: Array<{
    week: string;
    week_start_date: string;
    beneficiary_count: number;
    total_amount: {
      crores: number;
    };
  }>;
  amount_ranges: Array<{
    range: string;
    beneficiary_count: number;
    percentage: number;
  }>;
  pd_pipeline?: {
    total_benefs_in_system: number;
    pd_attempted: number;
    pd_succeeded: number;
    pd_failed: number;
    pd_never_attempted: number;
    pd_success_rate_pct: number;
    awaiting_md_approval: number;
    awaiting_md_aged_over_180d: number;
    blocked_pd_failed: number;
  };
  scheme_pipeline?: {
    md_approved: number;
    via_pd_success: number;
    via_failed_pd: number;
    no_pd_attempted: number;
    awaiting_bank_posting: number;
    started_stage_payment: number;
    any_stage_paid: number;
    all_three_stages_paid: number;
    marked_completed_in_excel: number;
    success_rate_pct: number;
    total_disbursed_cr: number;
    avg_per_paid_benef_lakh: number;
  };
  controls?: {
    paid_without_md_count: number;
    paid_without_md_amount_rs: number;
    by_channel: Record<string, number>;
    benef_ids: string[];
  };
}

const ExecutiveDashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    stage: '',
    payment_with: '',
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    console.log('🚀 ExecutiveDashboard mounted');
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    console.log('📡 Fetching dashboard with filters:', filters);
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiService.getExecutiveDashboard(filters);
      console.log('✅ Dashboard data received:', result);
      
      if (!result || !result.overview) {
        throw new Error('Invalid data structure');
      }
      
      setData(result);
    } catch (err: any) {
      console.error('❌ Error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    fetchDashboard();
  };

  const clearFilters = () => {
    setFilters({ stage: '', payment_with: '', startDate: '', endDate: '' });
    setTimeout(() => fetchDashboard(), 100);
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const formatCrores = (crores: number): string => {
    if (crores >= 1) {
      return `₹${crores.toFixed(2)}Cr`;
    } else if (crores >= 0.01) {
      return `₹${crores.toFixed(4)}Cr`;
    } else {
      return `₹${crores.toFixed(6)}Cr`;
    }
  };

  const formatPercentage = (percent: number): string => {
    return `${percent.toFixed(1)}%`;
  };

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#6366f1'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Loading Executive Dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center max-w-md p-6">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchDashboard}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center p-6">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No data available</p>
          <button
            onClick={fetchDashboard}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Refresh Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Executive Dashboard</h1>
              <p className="text-gray-600 mt-1">Housing Scheme Payment Analytics (All amounts in Crores)</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
              <button
                onClick={fetchDashboard}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stage</label>
                  <select
                    value={filters.stage}
                    onChange={(e) => setFilters({ ...filters, stage: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Stages</option>
                    <option value="BL">BL Only</option>
                    <option value="RL">BL → RL</option>
                    <option value="RC">BL → RL → RC</option>
                    <option value="COMPLETED">All Stages Complete</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                  <select
                    value={filters.payment_with}
                    onChange={(e) => setFilters({ ...filters, payment_with: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Methods</option>
                    <option value="APBS">APBS</option>
                    <option value="BANK">Kotak Bank</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Clear Filters
                </button>
                <button
                  onClick={applyFilters}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Beneficiary Pipeline & Amount */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Beneficiary Funnel */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Beneficiary Pipeline</h2>
                <p className="text-sm text-gray-500">Total registered to payment completed</p>
              </div>
            </div>

            {/* Funnel steps */}
            <div className="space-y-3">
              {/* Total */}
              <div className="relative">
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <span className="text-sm font-medium text-gray-700">Total Beneficiaries</span>
                    </div>
                    <span className="text-xl font-bold text-gray-900">{formatNumber(data.overview.total_beneficiaries)}</span>
                  </div>
                </div>
              </div>

              {/* Penny Drop */}
              <div className="ml-4 relative">
                <div className="absolute -left-4 top-0 bottom-0 w-px bg-gray-300"></div>
                <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                      <span className="text-sm font-medium text-gray-700">Penny Drop Verified</span>
                    </div>
                    <div className="text-right">
                      <span className="text-xl font-bold text-orange-600">{formatNumber(data.overview.unique_penny_drops_done)}</span>
                      <span className="text-gray-400 font-normal text-sm"> / {formatNumber(data.overview.total_beneficiaries_with_penny_drop_attempt)} attempted</span>
                    </div>
                  </div>
                  <div className="mt-1 flex items-center gap-4 text-xs">
                    <span className="text-red-500">{formatNumber(data.overview.total_beneficiaries_with_penny_drop_attempt - data.overview.unique_penny_drops_done)} failed</span>
                    <span className="text-blue-500">{formatNumber(data.overview.total_penny_drop_transactions - data.overview.total_beneficiaries_with_penny_drop_attempt)} reattempts</span>
                  </div>
                </div>
              </div>

              {/* Payment Success */}
              <div className="ml-8 relative">
                <div className="absolute -left-4 top-0 bottom-0 w-px bg-gray-300"></div>
                <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span className="text-sm font-medium text-gray-700">Payment Received</span>
                    </div>
                    <span className="text-xl font-bold text-green-600">{formatNumber(data.overview.successful_beneficiaries)}</span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {formatNumber(data.overview.total_successful_transactions)} payment transactions
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Total Amount Paid + Stage Breakdown */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <IndianRupeeIcon className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-600 mb-1">Successfully Paid</p>
            <p className="text-3xl font-bold text-gray-900">
              {formatCrores(data.overview.total_disbursed_amount.crores)}
            </p>
            <p className="text-xs text-gray-500 mt-1">{formatNumber(data.overview.total_successful_transactions)} transactions</p>
            {data.stage_wise && data.stage_wise.length > 0 && (
              <div className="mt-4 space-y-2 border-t pt-3">
                {data.stage_wise.map((s: any) => (
                  <div key={s.stage_code} className="flex justify-between text-sm">
                    <span className="text-gray-600">{s.stage_code}</span>
                    <div className="text-right">
                      <span className="font-semibold text-gray-900">{formatCrores(s.total_amount.crores)}</span>
                      <span className="text-xs text-gray-500 ml-2">{formatNumber(s.beneficiary_count)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Two-funnel meaningful view */}
        {(data.pd_pipeline || data.scheme_pipeline) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* PD Verification funnel */}
            {data.pd_pipeline && (
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-amber-100 rounded-lg">
                      <Target className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">PD Verification</h2>
                      <p className="text-sm text-gray-500">Upstream gate (₹1 account check)</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-amber-600">{formatNumber(data.pd_pipeline.pd_succeeded)}</p>
                    <p className="text-xs text-gray-500">PD succeeded</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between bg-gray-50 rounded p-2 border border-gray-200">
                    <span className="text-sm text-gray-700">Total in system</span>
                    <span className="font-bold text-gray-900">{formatNumber(data.pd_pipeline.total_benefs_in_system)}</span>
                  </div>
                  <div className="flex justify-between bg-blue-50 rounded p-2 border border-blue-200">
                    <span className="text-sm text-gray-700">PD attempted</span>
                    <span className="font-bold text-blue-700">{formatNumber(data.pd_pipeline.pd_attempted)}</span>
                  </div>
                  <div className="flex justify-between bg-green-50 rounded p-2 border border-green-200 ml-4">
                    <span className="text-sm text-gray-700">PD succeeded</span>
                    <span className="font-bold text-green-700">{formatNumber(data.pd_pipeline.pd_succeeded)}</span>
                  </div>
                  <div className="flex justify-between bg-red-50 rounded p-2 border border-red-200 ml-4">
                    <span className="text-sm text-gray-700">PD failed</span>
                    <span className="font-bold text-red-700">{formatNumber(data.pd_pipeline.pd_failed)}</span>
                  </div>
                </div>

              </div>
            )}

            {/* Scheme Pipeline funnel */}
            {data.scheme_pipeline && (
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-green-100 rounded-lg">
                      <Award className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">Scheme Pipeline</h2>
                      <p className="text-sm text-gray-500">Disbursement funnel (MD-approved cohort)</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-600">{formatNumber(data.scheme_pipeline.any_stage_paid)}</p>
                    <p className="text-xs text-gray-500">any stage paid</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="bg-blue-50 rounded p-2 border border-blue-200">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-700">MD approved</span>
                      <span className="font-bold text-blue-700">{formatNumber(data.scheme_pipeline.md_approved)}</span>
                    </div>
                    <div className="mt-1 grid grid-cols-3 gap-1 text-xs text-gray-600">
                      <span>via PD ✓: {formatNumber(data.scheme_pipeline.via_pd_success)}</span>
                      <span>via PD ✗: {formatNumber(data.scheme_pipeline.via_failed_pd)}</span>
                      <span>no PD: {formatNumber(data.scheme_pipeline.no_pd_attempted)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between bg-yellow-50 rounded p-2 border border-yellow-300 ml-4">
                    <span className="text-sm text-gray-700">⏳ Awaiting bank posting</span>
                    <span className="font-bold text-yellow-700">{formatNumber(data.scheme_pipeline.awaiting_bank_posting)}</span>
                  </div>
                  <div className="flex justify-between bg-gray-50 rounded p-2 border border-gray-200 ml-4">
                    <span className="text-sm text-gray-700">Stage payment started</span>
                    <span className="font-bold text-gray-700">{formatNumber(data.scheme_pipeline.started_stage_payment)}</span>
                  </div>
                  <div className="flex justify-between bg-green-50 rounded p-2 border border-green-200 ml-8">
                    <span className="text-sm text-gray-700">Any stage paid</span>
                    <span className="font-bold text-green-700">{formatNumber(data.scheme_pipeline.any_stage_paid)}</span>
                  </div>
                  <div className="flex justify-between bg-emerald-100 rounded p-2 border-2 border-emerald-400 ml-8">
                    <span className="text-sm font-medium text-gray-700">✓ All 3 stages paid</span>
                    <span className="font-bold text-emerald-700">{formatNumber(data.scheme_pipeline.all_three_stages_paid)}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs uppercase font-semibold text-gray-500">Disbursed</p>
                  <p className="text-xl font-bold text-purple-700">{formatCrores(data.scheme_pipeline.total_disbursed_cr)}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Stage-wise Distribution */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Stage-wise Distribution</h2>
            {data.stage_wise && data.stage_wise.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.stage_wise}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="stage_name" stroke="#6b7280" fontSize={12} />
                  <YAxis stroke="#6b7280" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    formatter={(value: any, name: string) => {
                      if (name === 'Beneficiaries') return [formatNumber(value), name];
                      if (name === 'Amount') return [formatCrores(value), name];
                      return [value, name];
                    }}
                  />
                  <Legend />
                  <Bar dataKey="beneficiary_count" fill="#3b82f6" name="Beneficiaries" />
                  <Bar dataKey={(item) => item.total_amount.crores} fill="#10b981" name="Amount" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-gray-500">
                No stage data available
              </div>
            )}
            <p className="text-xs text-gray-500 text-center mt-2">Amounts in Crores (Cr)</p>
          </div>

          {/* Payment Methods */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Methods</h2>
            {data.payment_methods && data.payment_methods.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data.payment_methods}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.payment_method === 'BANK' ? 'Kotak Bank' : entry.payment_method}: ${entry.beneficiary_count.toLocaleString('en-IN')}`}
                    outerRadius={100}
                    dataKey="beneficiary_count"
                  >
                    {data.payment_methods.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value: any, name: string, props: any) => {
                      if (name === 'Beneficiaries') {
                        return [
                          `${formatNumber(value)} (${formatCrores(props.payload.total_amount.crores)})`,
                          name
                        ];
                      }
                      return [formatNumber(value), name];
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-gray-500">
                No payment method data available
              </div>
            )}
          </div>
        </div>

        {/* Weekly Trend */}
        {data.weekly_trend && data.weekly_trend.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Weekly Trend</h2>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={data.weekly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="week_start_date" 
                  stroke="#6b7280"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis yAxisId="left" stroke="#6b7280" />
                <YAxis yAxisId="right" orientation="right" stroke="#6b7280" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value: any, name: string) => {
                    if (name === 'Beneficiaries') return [formatNumber(value), name];
                    if (name === 'Amount') return [formatCrores(value), name];
                    return [value, name];
                  }}
                />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="beneficiary_count"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  name="Beneficiaries"
                  dot={{ fill: '#3b82f6', r: 5 }}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey={(item) => item.total_amount.crores}
                  stroke="#10b981"
                  strokeWidth={3}
                  name="Amount"
                  dot={{ fill: '#10b981', r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
            <p className="text-xs text-gray-500 text-center mt-2">Amounts in Crores (Cr)</p>
          </div>
        )}

        {/* Amount Distribution */}
        {data.amount_ranges && data.amount_ranges.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Beneficiary Amount Distribution</h2>
            <p className="text-sm text-gray-600 mb-4">Distribution by total amount received per beneficiary</p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-4">
              {data.amount_ranges.map((range, index) => {
                const isOverpaid = range.range === '5L+';
                const isPennyDrop = range.range.includes('Penny');
                return (
                  <div key={index} className={`text-center p-4 rounded-xl border ${
                    isOverpaid ? 'bg-red-50 border-red-200'
                    : isPennyDrop ? 'bg-blue-50 border-blue-200'
                    : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'
                  }`}>
                    <p className="text-xs font-medium text-gray-600 mb-2">{range.range}</p>
                    <p className={`text-xl font-bold mb-1 ${
                      isOverpaid ? 'text-red-600' : isPennyDrop ? 'text-blue-600' : 'text-gray-900'
                    }`}>
                      {formatNumber(range.beneficiary_count)}
                    </p>
                  </div>
                );
              })}
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.amount_ranges}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="range" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value: any) => [formatNumber(value), 'Beneficiaries']}
                />
                <Bar dataKey="beneficiary_count" fill="#6366f1" name="Beneficiaries" />
              </BarChart>
            </ResponsiveContainer>
            <p className="text-xs text-gray-500 text-center mt-2">Individual beneficiary amounts shown in Lakhs (L)</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExecutiveDashboard;